import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Typography, useTheme } from '@mui/joy';

import { roundFloatToInt } from '../../../general-utilities/numbers-util';

interface CompassProps {
  windSpeed?: number;
  windDirection?: number;
  rotation?: number;
  isMobileLandscape?: boolean;
}

const Compass: React.FC<CompassProps> = ({
  windSpeed = 12,
  windDirection = 0,
  rotation = 0,
  isMobileLandscape = false
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });

  const { width } = containerSize;
  const padding = Math.max(8, width * 0.032);
  const arrowSize = Math.max(3, width * 0.01);
  const greenStartDegree = 345;
  const greenEndDegree = 375;

  // Calculate dimensions based on container size
  // Inner ring is 85% of the container (after padding)
  const innerRingSize = width > 0 ? (width - padding * 2) * 0.85 * 0.77 * (isMobileLandscape ? 1 / 0.766 : 1) : 0;
  const innerRingRadius = innerRingSize / 2;

  // Arrow should be positioned on the edge of the inner ring
  // The arrow base should be at the edge, pointing outward
  const windDirectionRad = (windDirection * Math.PI) / 180;

  // Position arrow on the edge of the inner ring (85% circle)
  // The arrow is positioned relative to the inner ring's center
  // Using 50% of the inner ring radius to position the arrow base at the edge
  const arrowRadius = innerRingRadius;
  const arrowX = Math.sin(windDirectionRad) * arrowRadius;
  const arrowY = -Math.cos(windDirectionRad) * arrowRadius;

  // Measure container size on mount, resize, and orientation change
  useEffect(() => {
    const updateSize = (): void => {
      if (containerRef.current) {
        const { width: newWidth, height } = containerRef.current.getBoundingClientRect();
        setContainerSize({ width: newWidth, height });
      }
    };

    // Use requestAnimationFrame to ensure layout has updated
    const updateSizeDelayed = (): void => {
      requestAnimationFrame(() => {
        requestAnimationFrame(updateSize);
      });
    };

    // Update size immediately
    updateSize();

    // Listen for resize events with debouncing to avoid too many updates
    let resizeTimeout: NodeJS.Timeout;
    const handleResize = (): void => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(updateSize, 100);
    };

    // Listen for orientation changes (use delayed update to ensure layout has changed)
    window.addEventListener('orientationchange', updateSizeDelayed);

    // Listen for resize events
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('orientationchange', updateSizeDelayed);
      window.removeEventListener('resize', handleResize);
      clearTimeout(resizeTimeout);
    };
  }, []);

  // Update container size when isMobileLandscape prop changes
  useEffect(() => {
    const updateSize = (): void => {
      if (containerRef.current) {
        const { width: newWidth, height } = containerRef.current.getBoundingClientRect();
        setContainerSize({ width: newWidth, height });
      }
    };

    // Use requestAnimationFrame to ensure layout has updated after prop change
    requestAnimationFrame(() => {
      requestAnimationFrame(updateSize);
    });
  }, [isMobileLandscape]);

  return (
    <Box
      ref={containerRef}
      className='relative w-full h-full'
      sx={{
        aspectRatio: '1 / 1',
        width: '100%',
        height: '100%',
        minWidth: 0,
        minHeight: 0
      }}
    >
      <Box
        className='overflow-hidden rounded-full relative'
        sx={{
          rotate: `${-rotation}deg`,
          padding: `${padding}px`,
          backgroundColor: 'color-mix(in srgb, var(--joy-palette-background-level1) 80%, transparent)',
          border: 1,
          borderColor: 'primary.offWhite',
          aspectRatio: '1 / 1',
          width: '100%',
          height: '100%',
          minWidth: 0,
          minHeight: 0
        }}
      >
        <Box
          className='z-10 rounded-full absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 relative'
          sx={{
            bgcolor: 'background.level3',
            border: 1,
            borderColor: 'neutral.400',
            width: '85%',
            aspectRatio: '1 / 1',
            minWidth: 0,
            minHeight: 0
          }}
        >
          {/* Wind direction arrow - positioned on the edge of the inner ring */}
          {width > 0 && innerRingRadius > 0 && (
            <Box
              className='absolute z-30'
              sx={{
                left: '50%',
                top: '50%',
                transform: `translate(calc(-50% + ${arrowX}px), calc(-50% + ${arrowY}px))`,
                transformOrigin: 'center center'
              }}
            >
              <Box
                style={{
                  rotate: `${windDirection}deg`,
                  width: 0,
                  height: 0,
                  borderLeft: `${arrowSize * 1.75}px solid transparent`,
                  borderRight: `${arrowSize * 1.75}px solid transparent`,
                  borderBottom: `${arrowSize * 1.75}px solid ${theme.palette.crew.compassWindRed}`
                }}
              />
            </Box>
          )}

          <Box
            className='absolute top-0 left-0 w-full h-full rounded-full'
            style={{
              rotate: `${rotation % 360}deg`,
              background: `conic-gradient(
                from ${greenStartDegree}deg,
                ${theme.palette.crew.compassGreen} 0deg,
                ${theme.palette.crew.compassGreen} ${greenEndDegree - greenStartDegree}deg,
                transparent ${greenEndDegree - greenStartDegree}deg,
                transparent 360deg
              )`,
              zIndex: 5
            }}
          />

          <Box
            className='z-10 rounded-full absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 flex flex-col items-center justify-center'
            sx={{
              bgcolor: 'background.level1',
              width: '77%',
              aspectRatio: '1 / 1',
              minWidth: 0,
              minHeight: 0
            }}
          >
            <Box style={{ rotate: `${rotation % 360}deg` }} className='flex flex-col items-center justify-center'>
              <Typography
                level='body-xs'
                sx={{
                  color: 'primary.offWhite'
                }}
              >
                {t('crew_view.indicators.compass.wind')}
              </Typography>
              <Typography
                level='h3'
                sx={{
                  color: 'primary.offWhite'
                }}
              >
                {roundFloatToInt(windSpeed)}
              </Typography>
              <Typography
                level='body-xs'
                sx={{
                  color: 'primary.offWhite'
                }}
              >
                {t('crew_view.units.mph')}
              </Typography>
            </Box>
          </Box>
        </Box>
        <Typography
          level='body-xs'
          className='absolute top-[2%] left-[50%]'
          sx={{
            color: 'primary.offWhite',
            rotate: '0deg',
            fontWeight: 700
          }}
        >
          {t('crew_view.indicators.compass.north')}
        </Typography>
        <Typography
          level='body-xs'
          className='absolute bottom-[2%] left-[50%]'
          sx={{
            color: 'primary.offWhite',
            rotate: '180deg',
            fontWeight: 700
          }}
        >
          {t('crew_view.indicators.compass.south')}
        </Typography>
        <Typography
          level='body-xs'
          className='absolute top-[43%] right-[5%]'
          sx={{
            color: 'primary.offWhite',
            rotate: '90deg',
            fontWeight: 700
          }}
        >
          {t('crew_view.indicators.compass.east')}
        </Typography>
        <Typography
          level='body-xs'
          className='absolute top-[45%] left-[3%]'
          sx={{
            color: 'primary.offWhite',
            rotate: '270deg',
            fontWeight: 700
          }}
        >
          {t('crew_view.indicators.compass.west')}
        </Typography>
        <Box
          className='absolute z-5 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-0 h-[90%] rotate-[45deg]'
          sx={{
            border: 1,
            borderColor: 'primary.offWhite'
          }}
        />
        <Box
          className='absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-0 h-[90%] rotate-[135deg]'
          sx={{
            border: 1,
            borderColor: 'primary.offWhite'
          }}
        />
      </Box>
      <Typography
        level='body-xs'
        className='rounded-full absolute top-[4%] left-[43%] flex items-center justify-center'
        sx={{
          backgroundColor: 'background.level1',
          color: 'primary.offWhite',
          width: '10%',
          aspectRatio: '1 / 1',
          minWidth: 0,
          minHeight: 0
        }}
      >
        {rotation % 360}°
      </Typography>
    </Box>
  );
};

export default Compass;
